import { useState } from 'react';
import { Clock, Minus, Plus } from 'lucide-react';
import {
  absoluteHourLabel,
  MAX_VIEW_HOURS,
  MAX_VIEW_START_HOUR,
  MIN_VIEW_HOURS,
  MIN_VIEW_START_HOUR,
  useDispatchBoard,
} from '../../lib/dispatch-board.js';
import { cn } from '../../lib/cn.js';

/**
 * Workday view picker for the dispatch board.
 *
 * Lets the dispatcher widen / shift the visible hour window without changing
 * how jobs are stored — FOP_START stays anchored at 8 AM, so pre-existing
 * assignments don't drift if the dispatcher pans the view.
 *
 * Renders three quick presets covering the common shop schedules and a
 * pair of -/+ steppers for fine-tuning. Picks persist to localStorage via
 * the dispatch zustand store, so each browser remembers the last window.
 */
const PRESETS: ReadonlyArray<{ label: string; startHour: number; hours: number }> = [
  { label: '8a–4p', startHour: 8, hours: 8 },
  { label: '7a–5p', startHour: 7, hours: 10 },
  { label: '6a–6p', startHour: 6, hours: 12 },
];

export function WorkdayPicker() {
  const viewStartHour = useDispatchBoard((s) => s.viewStartHour);
  const viewHours = useDispatchBoard((s) => s.viewHours);
  const setViewWindow = useDispatchBoard((s) => s.setViewWindow);

  const [open, setOpen] = useState(false);

  const endHour = viewStartHour + viewHours;
  const currentLabel = `${absoluteHourLabel(viewStartHour)} – ${absoluteHourLabel(endHour)}`;
  const activePreset = PRESETS.find(
    (p) => p.startHour === viewStartHour && p.hours === viewHours,
  );

  const canShiftEarlier = viewStartHour > MIN_VIEW_START_HOUR;
  const canShiftLater = viewStartHour < MAX_VIEW_START_HOUR;
  const canShrink = viewHours > MIN_VIEW_HOURS;
  const canGrow = viewHours < MAX_VIEW_HOURS;

  return (
    <div
      className="flex items-center gap-2 flex-wrap px-4 py-2 border-b text-xs"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <span className="inline-flex items-center gap-1 text-ink-muted">
        <Clock className="h-3.5 w-3.5" aria-hidden />
        <span className="font-medium uppercase tracking-wider text-[10px]">Workday</span>
      </span>

      <div className="flex items-center gap-1.5">
        {PRESETS.map((p) => (
          <button
            key={p.label}
            type="button"
            onClick={() => setViewWindow(p.startHour, p.hours)}
            aria-pressed={activePreset?.label === p.label}
            className={cn(
              'rounded-full border px-2.5 py-1 transition-colors',
              activePreset?.label === p.label
                ? 'bg-brand-50 border-brand-200 text-ink-accent font-medium'
                : 'bg-surface border-line-subtle text-ink-muted hover:text-ink',
            )}
          >
            {p.label}
          </button>
        ))}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-pressed={open}
          aria-expanded={open}
          className={cn(
            'rounded-full border px-2.5 py-1 transition-colors',
            !activePreset || open
              ? 'bg-brand-50 border-brand-200 text-ink-accent font-medium'
              : 'bg-surface border-line-subtle text-ink-muted hover:text-ink',
          )}
        >
          Custom
        </button>
      </div>

      <span className="text-[11px] text-ink-subtle ml-1">
        Showing <span className="text-ink font-medium">{currentLabel}</span>{' '}
        ({viewHours}h)
      </span>

      {open && (
        <div className="flex items-center gap-2 ml-auto">
          <Stepper
            label="Start"
            value={absoluteHourLabel(viewStartHour)}
            onDecrement={canShiftEarlier ? () => setViewWindow(viewStartHour - 1, viewHours) : undefined}
            onIncrement={canShiftLater ? () => setViewWindow(viewStartHour + 1, viewHours) : undefined}
          />
          <Stepper
            label="Width"
            value={`${viewHours}h`}
            onDecrement={canShrink ? () => setViewWindow(viewStartHour, viewHours - 1) : undefined}
            onIncrement={canGrow ? () => setViewWindow(viewStartHour, viewHours + 1) : undefined}
          />
        </div>
      )}
    </div>
  );
}

function Stepper({
  label,
  value,
  onDecrement,
  onIncrement,
}: {
  label: string;
  value: string;
  onDecrement?: () => void;
  onIncrement?: () => void;
}) {
  return (
    <div
      className="inline-flex items-stretch rounded-md border bg-surface overflow-hidden"
      style={{ borderColor: 'var(--color-border-tertiary)' }}
    >
      <span className="px-2 py-1 text-[10px] uppercase tracking-wider text-ink-subtle self-center">
        {label}
      </span>
      <button
        type="button"
        onClick={onDecrement}
        disabled={!onDecrement}
        aria-label={`Decrease ${label.toLowerCase()}`}
        className="inline-flex h-7 w-7 items-center justify-center border-l text-ink-muted hover:bg-surface-muted disabled:opacity-30 disabled:cursor-not-allowed"
        style={{ borderColor: 'var(--color-border-tertiary)' }}
      >
        <Minus className="h-3 w-3" aria-hidden />
      </button>
      <span
        className="px-2 py-1 text-xs font-medium text-ink tabular-nums min-w-[56px] text-center border-l border-r self-center"
        style={{ borderColor: 'var(--color-border-tertiary)' }}
      >
        {value}
      </span>
      <button
        type="button"
        onClick={onIncrement}
        disabled={!onIncrement}
        aria-label={`Increase ${label.toLowerCase()}`}
        className="inline-flex h-7 w-7 items-center justify-center text-ink-muted hover:bg-surface-muted disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <Plus className="h-3 w-3" aria-hidden />
      </button>
    </div>
  );
}
